// src/SubscribeSuccess.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Layout from "./Components/Layout";
import { api } from "./lib/api";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function SubscribeSuccess() {
  const q = useQuery();
  const nav = useNavigate();
  const session_id = q.get("session_id");
  const plan = q.get("plan") || "yearly";

  const ranRef = useRef(false);

  const [step, setStep] = useState({
    paid: "idle",     // idle | loading | done | error
    account: "idle",
    email: "idle",
  });

  const [msg, setMsg] = useState("");
  const [emailSent, setEmailSent] = useState(null); // null/true/false

  useEffect(() => {
    if (!session_id) {
      setMsg("Missing session id.");
      setStep({ paid: "error", account: "error", email: "error" });
      return;
    }

    if (ranRef.current) return;       // ✅ stops endless loop
    ranRef.current = true;

    (async () => {
      try {
        setMsg("");
        setStep({ paid: "loading", account: "idle", email: "idle" });

        // Payment already succeeded if Stripe redirected here, so mark as done fast
        setStep((p) => ({ ...p, paid: "done", account: "loading" }));

        const { data } = await api.post("/api/payments/fulfill", {
          session_id,
          plan,
        });

        if (!data?.ok) throw new Error(data?.message || "Unable to finalise your account.");

        setStep((p) => ({ ...p, account: "done", email: "loading" }));

        // Email step is “done” whether it was sent or not (but we message accordingly)
        setEmailSent(!!data?.emailSent);
        setStep((p) => ({ ...p, email: "done" }));

        setMsg(
          data?.createdUser
            ? "Thank you for subscribing to Chinese Horoscope. Please check your email to log in. Enjoy."
            : "Thank you! Your subscription is active."
        );
      } catch (e) {
        setMsg(e?.response?.data?.message || e?.message || "Something went wrong.");
        setStep({ paid: "done", account: "error", email: "error" });
      }
    })();
  }, [session_id, plan]);

  return (
    <Layout title="Payment received">
      <div style={{ maxWidth: 720, margin: "70px auto", padding: 20, textAlign: "center" }}>
        <h2 style={{ marginBottom: 8 }}>Payment received</h2>

        <div style={{ display: "grid", gap: 10, justifyContent: "center", marginTop: 18 }}>
          <StepRow label="Payment received" state={step.paid} />
          <StepRow label="Creating / updating account" state={step.account} />
          <StepRow label="Email sent with login details" state={step.email} />
        </div>

        {msg ? (
          <div style={{ marginTop: 18, color: step.account === "error" ? "#b00020" : "#1b7f3a", fontWeight: 800 }}>
            {msg}
            {emailSent === false ? (
              <div style={{ marginTop: 6, color: "#555", fontWeight: 600, fontSize: 13 }}>
                (Email not sent — please check SMTP settings in your server .env)
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ marginTop: 18, display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => nav("/login")} style={btnOutline}>Login</button>
          <button onClick={() => nav("/account")} style={btnOutline}>Go to your account</button>
          <Link to="/" style={btnLink}>Home</Link>
        </div>
      </div>
    </Layout>
  );
}

function StepRow({ label, state }) {
  const icon =
    state === "done" ? "✅" :
    state === "error" ? "❌" :
    state === "loading" ? "🔄" : "⏳";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800 }}>
      <span style={{ width: 28, textAlign: "center" }}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

const btnOutline = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.18)",
  background: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const btnLink = {
  padding: "10px 14px",
  borderRadius: 12,
  fontWeight: 900,
  color: "#111",
  textDecoration: "none",
};
